/**
 * VOICEセクションの横カルーセル制御
 *
 * @description スクロールスナップで動く一覧に、矢印ボタンと「スクロールバー」を連動させる。
 *              バーはドラッグ・クリックで操作できる本物のスクロールバーとして動作する。
 *              スワイプでもボタンでもバーでも状態が揃うよう、常にscrollLeftを基準に描画する。
 */

/**
 * 1つのカルーセルを初期化する
 * @param {HTMLElement} root - [data-voice] を持つセクション要素
 */
const initVoice = (root: HTMLElement): void => {
  const list = root.querySelector<HTMLElement>("[data-voice-list]")
  const track = root.querySelector<HTMLElement>("[data-voice-track]")
  const thumb = root.querySelector<HTMLElement>("[data-voice-thumb]")
  const prev = root.querySelector<HTMLButtonElement>("[data-voice-prev]")
  const next = root.querySelector<HTMLButtonElement>("[data-voice-next]")

  if (!list || !track || !thumb || !prev || !next) return

  /**
   * スクロールできる最大量
   * @returns {number} px
   */
  const maxScroll = (): number => Math.max(0, list.scrollWidth - list.clientWidth)

  /**
   * カード1枚分の移動量（カード幅＋隙間）を求める
   * @returns {number} スクロール量(px)
   */
  const step = (): number => {
    const items = list.querySelectorAll<HTMLElement>(".voice__listItem")
    if (items.length < 2) return list.clientWidth
    return items[1].offsetLeft - items[0].offsetLeft
  }

  /**
   * つまみの位置とボタンの活性状態を、現在のスクロール位置から更新する
   * @description つまみの幅はデザイン固定なのでCSSが持つ（.voice__barThumb）。
   *              ここではトラックの余り幅ぶんだけ左右に動かす。
   */
  const update = (): void => {
    const max = maxScroll()
    const progress = max > 0 ? list.scrollLeft / max : 0
    const room = Math.max(0, track.clientWidth - thumb.getBoundingClientRect().width)

    thumb.style.transform = `translateX(${room * progress}px)`

    prev.disabled = list.scrollLeft <= 1
    next.disabled = list.scrollLeft >= max - 1
  }

  /**
   * トラック上の位置に応じてスクロール位置を決める
   * @param {number} clientX - ポインタのX座標
   */
  const scrollToPointer = (clientX: number): void => {
    const rect = track.getBoundingClientRect()
    const thumbW = thumb.getBoundingClientRect().width
    // つまみの中央をポインタに合わせる
    const usable = rect.width - thumbW
    if (usable <= 0) return

    const pos = clientX - rect.left - thumbW / 2
    const ratio = Math.min(1, Math.max(0, pos / usable))
    list.scrollLeft = ratio * maxScroll()
  }

  prev.addEventListener("click", () => {
    list.scrollBy({ left: -step(), behavior: "smooth" })
  })

  next.addEventListener("click", () => {
    list.scrollBy({ left: step(), behavior: "smooth" })
  })

  // スクロールバーのドラッグ操作
  let dragging = false

  track.addEventListener("pointerdown", (e) => {
    dragging = true
    track.setPointerCapture(e.pointerId)
    track.classList.add("is-dragging")
    scrollToPointer(e.clientX)
  })

  track.addEventListener("pointermove", (e) => {
    if (!dragging) return
    e.preventDefault()
    scrollToPointer(e.clientX)
  })

  const endDrag = (e: PointerEvent) => {
    if (!dragging) return
    dragging = false
    track.classList.remove("is-dragging")
    if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId)
  }

  track.addEventListener("pointerup", endDrag)
  track.addEventListener("pointercancel", endDrag)

  list.addEventListener("scroll", update, { passive: true })
  window.addEventListener("resize", update)

  update()
}

document.querySelectorAll<HTMLElement>("[data-voice]").forEach(initVoice)
